import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Chip,
  Stack
} from '@mui/material';
import Business from '@mui/icons-material/Business';
import { CompanyData } from '../helpers/CompanyStorage';

interface CompanySelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectCompany: (company: CompanyData) => void;
  companies: CompanyData[];
  loading?: boolean;
  disableClose?: boolean;
  /** Khi đổi công ty: khóa công ty đang làm việc */
  disabledCompanyId?: string;
}

const CompanySelectionModal: React.FC<CompanySelectionModalProps> = ({
  open,
  onClose,
  onSelectCompany,
  companies,
  loading = false,
  disableClose = false,
  disabledCompanyId,
}) => {
  const normalizedDisabledCompanyId = String(disabledCompanyId || '').trim();

  const isCompanyDisabled = (company: CompanyData) => {
    if (!normalizedDisabledCompanyId) {
      return false;
    }
    return String(company.company_id) === normalizedDisabledCompanyId;
  };

  const handleSelectCompany = (company: CompanyData) => {
    if (isCompanyDisabled(company)) {
      return;
    }
    onSelectCompany(company);
  };

  const handleDialogClose = (
    _event: object,
    reason?: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (disableClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }

    onClose();
  };


  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={disableClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Business color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" component="div" fontWeight="600">
            Chọn Công Ty
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ px: 0, py: 0, mt: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography>Đang tải danh sách công ty...</Typography>
            </Box>
          ) : (
            <Stack spacing={3}>
              {companies.map((company) => {
                const isDisabled = isCompanyDisabled(company);

                return (
                  <Card
                    key={company.company_id}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: isDisabled ? 'action.disabled' : 'divider',
                      opacity: isDisabled ? 0.65 : 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:not(:last-child)': {
                        mb: 2,
                      },
                      ...(!isDisabled && {
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
                          transform: 'translateY(-2px)',
                        },
                      }),
                    }}
                  >
                    <CardActionArea
                      disabled={isDisabled}
                      onClick={() => handleSelectCompany(company)}
                      sx={{
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2} sx={{ py: 0.5 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: isDisabled ? 'action.disabled' : 'primary.main',
                              fontSize: '1.15rem',
                            }}
                          >
                            <Business sx={{ fontSize: 18 }} />
                          </Avatar>

                          <Box flex={1}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                color: isDisabled ? 'text.disabled' : 'text.primary',
                              }}
                            >
                              {company.company_name}
                            </Typography>
                          </Box>

                          {isDisabled && (
                            <Chip label="Đang làm việc" size="small" color="default" variant="outlined" />
                          )}
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>

      </DialogContent>

      {!disableClose && (
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderRadius: 1.5
            }}
          >
            Đóng
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CompanySelectionModal;
